import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronRightIcon,
  PhotoIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { FacilitiesService, FACILITY_TYPES, FACILITY_CATEGORIES } from '../../services/facilitiesService';

const FacilitiesPage = () => {
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const facilityTypeLabels = {
    [FACILITY_TYPES.GYMNASIUM]: 'Gymnasium',
    [FACILITY_TYPES.FIELD]: 'Athletic Field',
    [FACILITY_TYPES.TRAINING]: 'Training Facility',
    [FACILITY_TYPES.LOCKER_ROOM]: 'Locker Room',
    [FACILITY_TYPES.STORAGE]: 'Storage',
    [FACILITY_TYPES.OTHER]: 'Other'
  };

  const filterOptions = [
    { value: 'all', label: 'All Facilities', icon: BuildingOfficeIcon },
    { value: FACILITY_TYPES.GYMNASIUM, label: 'Gymnasiums', icon: BuildingOfficeIcon },
    { value: FACILITY_TYPES.FIELD, label: 'Athletic Fields', icon: MapPinIcon },
    { value: FACILITY_TYPES.TRAINING, label: 'Training Areas', icon: WrenchScrewdriverIcon },
    { value: FACILITY_TYPES.LOCKER_ROOM, label: 'Locker Rooms', icon: UsersIcon }
  ];

  useEffect(() => {
    loadFacilities();
  }, [activeFilter]);

  const loadFacilities = async () => {
    try {
      setLoading(true);
      const filters = activeFilter !== 'all' ? { type: activeFilter } : {};
      const data = await FacilitiesService.getFacilities(filters);
      setFacilities(data);
    } catch (error) {
      console.error('Error loading facilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time) => {
    return FacilitiesService.formatTime(time);
  };

  const getDaySchedule = (facility, day) => {
    const schedule = facility.availability[day];
    if (!schedule || schedule.length === 0) {
      return 'Closed';
    }
    return schedule.map(slot => `${formatTime(slot.start)} - ${formatTime(slot.end)}`).join(', ');
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const renderFacilityCard = (facility) => (
    <div key={facility.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      {/* Facility Image */}
      <div className="relative h-48 bg-gray-200">
        {facility.images && facility.images.length > 0 ? (
          <img
            src={facility.images[0]}
            alt={facility.name}
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => openImageModal(facility.images[0])}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <PhotoIcon className="h-16 w-16 text-gray-400" />
          </div>
        )}
        
        {/* Facility Type Badge */}
        <div className="absolute top-4 left-4">
          <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
            {facilityTypeLabels[facility.type]}
          </span>
        </div>
        
        {/* Capacity Badge */}
        {facility.capacity && (
          <div className="absolute top-4 right-4">
            <span className="bg-white bg-opacity-90 text-gray-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
              <UsersIcon className="h-4 w-4 mr-1" />
              {facility.capacity}
            </span>
          </div>
        )}
      </div>
      
      {/* Facility Info */}
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{facility.name}</h3>
        <p className="text-gray-600 mb-4">{facility.description}</p>
        
        {/* Key Details */}
        <div className="space-y-2 mb-4">
          {facility.dimensions && (
            <div className="flex items-center text-sm text-gray-600">
              <MapPinIcon className="h-4 w-4 mr-2" />
              <span>Dimensions: {facility.dimensions}</span>
            </div>
          )}
          {facility.surface && (
            <div className="flex items-center text-sm text-gray-600">
              <InformationCircleIcon className="h-4 w-4 mr-2" />
              <span>Surface: {facility.surface}</span>
            </div>
          )}
        </div>
        
        {/* Contact Info */}
        {facility.contact && (
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex items-center">
                <UsersIcon className="h-4 w-4 mr-2" />
                <span>{facility.contact.manager}</span>
              </div>
              <div className="flex items-center">
                <PhoneIcon className="h-4 w-4 mr-2" />
                <span>{facility.contact.phone}</span>
              </div>
              <div className="flex items-center">
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                <span>{facility.contact.email}</span>
              </div>
            </div>
          </div>
        )}
        
        {/* View Details Button */}
        <div className="mt-6">
          <button
            onClick={() => setSelectedFacility(facility)}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <span>View Details</span>
            <ChevronRightIcon className="h-4 w-4 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );

  const renderFacilityDetail = () => {
    if (!selectedFacility) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{selectedFacility.name}</h2>
            <button
              onClick={() => setSelectedFacility(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="p-6">
            {/* Image Gallery */}
            {selectedFacility.images && selectedFacility.images.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Photos</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedFacility.images.map((image, index) => (
                    <img
                      key={index}
                      src={image}
                      alt={`${selectedFacility.name} ${index + 1}`}
                      className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageModal(image)}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Facility Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className="font-medium">{facilityTypeLabels[selectedFacility.type]}</span>
                  </div>
                  {selectedFacility.capacity && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Capacity:</span>
                      <span className="font-medium">{selectedFacility.capacity} people</span>
                    </div>
                  )}
                  {selectedFacility.dimensions && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dimensions:</span>
                      <span className="font-medium">{selectedFacility.dimensions}</span>
                    </div>
                  )}
                  {selectedFacility.surface && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surface:</span>
                      <span className="font-medium">{selectedFacility.surface}</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                {selectedFacility.contact && (
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <span>{selectedFacility.contact.manager}</span>
                    </div>
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <a href={`tel:${selectedFacility.contact.phone}`} className="text-blue-600 hover:text-blue-800">
                        {selectedFacility.contact.phone}
                      </a>
                    </div>
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <a href={`mailto:${selectedFacility.contact.email}`} className="text-blue-600 hover:text-blue-800">
                        {selectedFacility.contact.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Features */}
            {selectedFacility.features && selectedFacility.features.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedFacility.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Amenities */}
            {selectedFacility.amenities && selectedFacility.amenities.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Amenities</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {selectedFacility.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Operating Hours */}
            {selectedFacility.availability && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Hours</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(selectedFacility.availability).map(([day, schedule]) => (
                      <div key={day} className="flex justify-between items-center">
                        <span className="font-medium capitalize text-gray-900">
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </span>
                        <span className="text-gray-600">
                          {getDaySchedule(selectedFacility, day)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Usage Guidelines */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">Usage Guidelines</h3>
              <div className="space-y-2 text-blue-800">
                <p>• All facility usage must be scheduled in advance through the Athletic Department</p>
                <p>• Proper athletic attire and footwear required</p>
                <p>• Adult supervision required for all student activities</p>
                <p>• Clean up after use - leave facilities better than you found them</p>
                <p>• Report any damage or maintenance issues immediately</p>
                <p>• No food or drinks (except water) in gymnasium and training areas</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Athletic Facilities
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
              Explore our state-of-the-art athletic facilities designed to support 
              excellence in sports and student development.
            </p>
          </div>
        </div>
      </div>

      {/* Filter Navigation */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto py-4">
            {filterOptions.map((option) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.value}
                  onClick={() => setActiveFilter(option.value)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap transition-colors ${
                    activeFilter === option.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Facilities Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {activeFilter === 'all' ? 'All Facilities' : filterOptions.find(f => f.value === activeFilter)?.label}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our facilities are designed to provide the best possible environment for athletic training, 
                competition, and student development.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {facilities.map(renderFacilityCard)}
            </div>
            
            {facilities.length === 0 && (
              <div className="text-center py-12">
                <BuildingOfficeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No facilities found</h3>
                <p className="text-gray-600">Try adjusting your filter selection.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Facility Booking Information */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Facility Booking & Information
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Interested in using our facilities? Contact our Athletic Department for scheduling and availability.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <ClockIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Operating Hours</h3>
              <p className="text-gray-600">Monday - Friday: 6:00 AM - 10:00 PM<br />Weekends: 8:00 AM - 8:00 PM</p>
            </div>
            
            <div className="text-center">
              <PhoneIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Us</h3>
              <p className="text-gray-600">
                <a href="tel:(615) 555-0100" className="text-blue-600 hover:text-blue-800">
                  (615) 555-0100
                </a>
              </p>
            </div>
            
            <div className="text-center">
              <EnvelopeIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
              <p className="text-gray-600">
                <a href="mailto:athletics@eastendprep.edu" className="text-blue-600 hover:text-blue-800">
                  athletics@eastendprep.edu
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Facility Detail Modal */}
      {selectedFacility && renderFacilityDetail()}

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <img
              src={selectedImage}
              alt="Facility"
              className="max-w-full max-h-full object-contain"
            />
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilitiesPage;